import type { FormSettings, Currency } from '../form-settings'

export type Pledge = {
  amount: number
  accountId: string
  totalPaid: number
  currency: Currency | 'NIS'
  billingPlan: any
  name: string
  lastName: string
  address: any
  email: any
  phone: any
  cardsOnFile: any[]
  campaignId: string
  campaignName: string
  subCampaign: string
  subCampaignName: string
  department: string
}

export type PledgeQueryParams = Record<string, unknown>
export type PledgeFormSetter = (field: string, value: unknown) => void
export type PledgeLookupParams = {
  id: string
  accountId: string
}

function queryParamValue(value: unknown) {
  if (Array.isArray(value)) return value[0] == null ? '' : String(value[0])
  return value == null ? '' : String(value)
}

export function getPledgeId(params: PledgeQueryParams) {
  return queryParamValue(params.id)
}

export function getPledgeAccountId(params: PledgeQueryParams) {
  return queryParamValue(params.accountId || params.accountid)
}

export function validatePledgeLookupParams(params: PledgeQueryParams): PledgeLookupParams {
  const id = getPledgeId(params).trim()
  const accountId = getPledgeAccountId(params).trim()

  if (!id && !accountId) {
    throw new Error('Pledge form requires id and accountId (or accountid) query parameters.')
  }

  if (!id) {
    throw new Error('Pledge form requires an id query parameter.')
  }

  if (!accountId) {
    throw new Error('Pledge form requires an accountId (or accountid) query parameter.')
  }

  return { id, accountId }
}

export function buildPledgeLookupUrl(params: PledgeQueryParams) {
  const { id, accountId } = validatePledgeLookupParams(params)

  return `https://velocity2.ouapis.org/donation-forms/pledge?id=${encodeURIComponent(id)}&accountId=${encodeURIComponent(accountId)}`
}

export async function fetchPledge(
  params: PledgeQueryParams,
  fetcher: typeof fetch = fetch
) {
  const response = await fetcher(buildPledgeLookupUrl(params))
  if (!response.ok) {
    throw new Error(`Failed to load pledge (${response.status})`)
  }

  return await response.json() as Pledge
}

export function normalizePledgeCurrency(currency: Pledge['currency']) {
  return currency === 'NIS' ? 'ILS' : currency
}

export function calculateFutureInstallmentsTotal(
  billingPlan: Pledge['billingPlan'],
  now = new Date()
) {
  const installments = Array.isArray(billingPlan?.installments)
    ? billingPlan.installments
    : []

  return installments
    .filter((installment: any) => new Date(installment.date) > now)
    .reduce((total: number, installment: any) => total + Number(installment.amount || 0), 0)
}

export function hasActiveBillingPlan(
  billingPlan: Pledge['billingPlan'],
  now = new Date()
) {
  const installments = Array.isArray(billingPlan?.installments)
    ? billingPlan.installments
    : []

  return installments.some(
    (installment: any) => !installment.paid && new Date(installment.date) > now
  )
}

export function calculatePledgeAmountDue(pledge: Pledge, now = new Date()) {
  return pledge.amount - pledge.totalPaid - calculateFutureInstallmentsTotal(pledge.billingPlan, now)
}

export function toProvidedPledge(pledge: Pledge): Pledge {
  return {
    ...pledge,
    campaignId: '',
    campaignName: pledge.campaignName || 'General Donation',
    subCampaign: '',
    subCampaignName: pledge.subCampaignName || ''
  }
}

export function applyPledgeToPaymentForm(
  pledge: Pledge,
  params: PledgeQueryParams,
  setValue: PledgeFormSetter,
  settings?: FormSettings
) {
  setValue('applications.0.department', pledge.department || settings?.department || '')
  setValue('applications.0.campaign', pledge.campaignName || 'General Donation')
  setValue('applications.0.subCampaign', pledge.subCampaignName || '')
  setValue('accountID', pledge.accountId)
  setValue('amount', String(calculatePledgeAmountDue(pledge)))
  setValue('firstName', pledge.lastName ? pledge.name : null)
  setValue('lastName', pledge.lastName)
  setValue('email', pledge.email?.email)
  setValue('phone', pledge.phone?.phone)
  setValue('country', pledge.address?.country)
  setValue('street', pledge.address?.street)
  setValue('street2', pledge.address?.street2 || null)
  setValue('city', pledge.address?.city)
  setValue('state', pledge.address?.state || null)
  setValue('zip', pledge.address?.zip)
  setValue('currency', normalizePledgeCurrency(pledge.currency))
  setValue('pledgeId', getPledgeId(params))

  return toProvidedPledge(pledge)
}
